import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { apiErrorMessage } from '../lib/apiError';
import { Loader2, Send, ShieldCheck } from 'lucide-react';
import { Input } from './ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Inline phone verification — wraps a phone input with a "Verify" button.
 * Calls `/api/customer/phone/request-verify` then `/api/customer/phone/confirm-verify`.
 * On success calls `onVerified(phone)`. The parent owns the phone value so
 * checkout / profile forms can keep using their own input — we just render
 * the badge + code-entry mini-form alongside.
 *
 * Hides itself entirely (just renders the phone input) when the admin has
 * `notifications.sms_enabled=false` OR `require_phone_verify_at_checkout=false`.
 */
const PhoneVerifyInline = ({ phone, email, onChange, onVerified, verified, required = false, testId = 'phone-verify' }) => {
  const [enabled, setEnabled] = useState(false);
  const [stage, setStage] = useState('idle'); // idle | sending | code | verifying | done | error
  const [code, setCode] = useState('');
  const [masked, setMasked] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [autoTried, setAutoTried] = useState(false); // guards the one-shot auto-verify
  // True only when the user just transitioned from unverified → verified in
  // this session. Drives the one-shot pulse animation so a pill that was
  // already verified on mount (page refresh) doesn't keep re-pulsing.
  const justVerified = stage === 'done';

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/auth/sms-otp-config`);
        // We rely on the sms_master_enabled flag; phone-verify gating is
        // server-side at checkout, but the UI shows the Verify button
        // whenever SMS sending is on (so customers can opt-in).
        if (mounted) setEnabled(!!data?.sms_master_enabled);
      } catch {
        if (mounted) setEnabled(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const sendCode = async (e) => {
    e?.preventDefault?.();
    if (!phone || phone.replace(/\D/g, '').length < 7) {
      setErrorMsg('Enter a valid phone number first');
      return;
    }
    setStage('sending');
    setErrorMsg('');
    try {
      const { data } = await axios.post(`${API}/customer/phone/request-verify`, { phone });
      if (data?.already_verified) {
        onVerified?.(phone);
        setStage('done');
        return;
      }
      setMasked(data?.masked_phone || '');
      setStage('code');
    } catch (err) {
      setStage('error');
      setErrorMsg(apiErrorMessage(err, 'Failed to send code'));
    }
  };

  const confirmCode = async (e, explicitCode) => {
    e?.preventDefault?.();
    // Auto-verify passes the just-typed code explicitly because React state
    // (`code`) hasn't propagated yet inside the same render cycle.
    const c = (explicitCode != null ? explicitCode : code) || '';
    if (c.length !== 4) {
      setErrorMsg('Enter the 4-digit code');
      return;
    }
    setStage('verifying');
    setErrorMsg('');
    try {
      await axios.post(`${API}/customer/phone/confirm-verify`, { phone, code: c, email: email || null });
      setStage('done');
      onVerified?.(phone);
    } catch (err) {
      setStage('code');
      setErrorMsg(apiErrorMessage(err, 'Invalid code'));
    }
  };

  // If admin hasn't enabled SMS at all, render nothing — parent's plain phone input stays.
  if (!enabled) return null;

  // Once verified, the Verify pill turns green and reads "VERIFIED" — keeps
  // the same shape/position as the blue button so the user sees a direct
  // state change rather than a separate badge appearing elsewhere.
  if (verified || stage === 'done') {
    return (
      <div className="mt-1.5" data-testid={`${testId}-card`}>
        <button
          type="button"
          disabled
          aria-disabled="true"
          className={`inline-flex items-center gap-1.5 h-8 px-4 rounded-full bg-emerald-600 text-white text-[11px] font-bold uppercase tracking-wider cursor-default shadow-sm${justVerified ? ' verified-pulse' : ''}`}
          data-testid={`${testId}-verified`}
        >
          <ShieldCheck className="w-3.5 h-3.5" />
          Verified
        </button>
      </div>
    );
  }

  return (
    <div className="mt-1.5 space-y-1.5" data-testid={`${testId}-card`}>
      {stage !== 'code' && (
        <button
          type="button"
          onClick={sendCode}
          disabled={stage === 'sending' || !phone}
          className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full bg-[#EFF6FC] hover:bg-[#DEECF9] border border-[#BBDCF7] text-[#0E4A7B] text-[11px] font-bold uppercase tracking-wider disabled:opacity-60"
          data-testid={`${testId}-send-code`}
        >
          {stage === 'sending' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
          {required ? 'Verify phone (required)' : 'Verify phone'}
        </button>
      )}
      {stage === 'code' && (
        <div className="flex items-center gap-1.5">
          <Input
            inputMode="numeric"
            maxLength={4}
            value={code}
            onChange={(e) => {
              const next = e.target.value.replace(/\D/g, '').slice(0, 4);
              setCode(next);
              if (errorMsg) setErrorMsg('');
              // Auto-verify the moment 4 digits land — saves the user a tap
              // and mirrors the email-OTP login flow. Guarded by `autoTried`
              // so a wrong code doesn't keep re-firing as the user clears
              // and retypes.
              if (next.length === 4 && !autoTried && stage !== 'verifying') {
                setAutoTried(true);
                setTimeout(() => confirmCode(null, next), 80);
              }
              if (next.length < 4 && autoTried) setAutoTried(false);
            }}
            onKeyDown={(e) => {
              // Enter inside this input would submit the OUTER checkout
              // form (placing the order before phone is even verified).
              // Intercept it and run the local verify handler instead.
              if (e.key === 'Enter') {
                e.preventDefault();
                if (code.length === 4 && stage !== 'verifying') confirmCode(e);
              }
            }}
            placeholder="1234"
            className="h-8 w-24 text-center text-base font-extrabold tracking-[0.3em]"
            data-testid={`${testId}-code-input`}
            autoFocus
          />
          {stage === 'verifying' ? (
            <span className="inline-flex items-center gap-1 h-8 px-3 rounded-full bg-[#0078D4] text-white text-[11px] font-bold uppercase tracking-wider">
              <Loader2 className="w-3 h-3 animate-spin" />
              Verifying
            </span>
          ) : (
            <span className="text-[11px] text-slate-500 font-medium">
              Auto-verify when 4 digits entered
            </span>
          )}
          {masked && (
            <span className="text-[10px] text-slate-500 ml-1">sent to {masked}</span>
          )}
        </div>
      )}
      {errorMsg && (
        <div className="text-[10px] text-red-600 font-medium" data-testid={`${testId}-error`}>{errorMsg}</div>
      )}
    </div>
  );
};

export default PhoneVerifyInline;
